import moment from 'moment';
import { format, differenceInMilliseconds } from 'date-fns';

/**
 * Format date for note
 * @param {string} date // '2019-08-04T04:08:30+02:00'
 */
const getNoteDate = (date) => {
  return format(new Date(date), 'MMM dd, yyyy');
};

/**
 * Format date to US style - MM/DD/YYYY
 * Prevents adjustment of date based on browser timezone
 * @param {string} date // '2021-03-09T00:00:00.000Z'
 */
const getUSFormatDate = (date) => {
  return format(new Date(date.substring(0, 10).replace(/-/g, '/')), 'MM/dd/yyyy');
};

/**
 * Sort array by date
 * @param {array} arr
 */
const sortByDate = (arr) =>
  arr.sort((a, b) => differenceInMilliseconds(new Date(b.lastUpdatedTime), new Date(a.lastUpdatedTime)));

/**
 * Format date for note details
 * @param {string} date
 */
const formatNoteDetailsDate = (date) => moment(date).format('MMM DD, YYYY');
const formatNoteDetailsDateTime = (date) => moment(date).format('MMM DD, YYYY [at] h:mm A');

/**
 *
 * @param {number} days
 * @returns {object} - {from: '2019-05-20', to: '2019-05-25'}
 */
const getPeriodByDays = (days = 0) => {
  const to = moment().format('YYYY-MM-DD');
  const from = moment()
    .subtract(days, 'days')
    .format('YYYY-MM-DD');
  return { to, from };
};

const getStartDate = (days = 0) => moment().subtract(days, 'days');

/**
 * checks if timestamp is valid
 * @param {any} timestamp
 * @returns {boolean}
 */
const isValidTimeStamp = (timestamp) => typeof timestamp === 'number' && !!new Date(timestamp).getTime();

/**
 * creates timestamp from date-string in given format
 * @param {strin} date
 * @param {string} ?format 'yyyy-mm-dd'
 */
const getTimestampFromDateString = (date, format = 'YYYY-MM-DD') => {
  const now = moment.now();
  const timestamp = new Date(moment(date, format)).getTime();

  return isValidTimeStamp(timestamp) ? timestamp : now;
};

/**
 * Check validity of date object
 * Returns provided date if date is formatted correctly for a valid date obj
 * Return false if provided date is not correct format for a valid date obj
 */
const checkDateValidity = (date) => !!moment(date).isValid();

/**
 * Format date to mm/dd/yyyy
 * @param {string} date
 * @param {string} format
 */
const formatDateMMDDYYYY = (date, format) => moment(getTimestampFromDateString(date, format)).format('MM/DD/YYYY');

/**
 * Format date to yyyy/mm/dd
 * @param {string} date
 * @param {string} format
 */
const formatDateYYYYMMDD = (date) => moment(date).format('YYYY-MM-DD');

/**
 *  Format date to MMMM D - January 1
 * @param {date} stamp
 */
const formatDateMMMMD = (date) => moment(date).format('MMMM D');

/**
 * converts timestamps to relative-date string
 * @param {any} stamp timestamp
 * @returns {string} relative date
 */
const timeStampToRelativeDate = (stamp) => {
  const currentDate = moment(isValidTimeStamp(stamp) ? stamp : undefined);
  return moment(currentDate).fromNow();
};

/**
 * Sort array by timestamp value
 * @param {array} array
 * @param {function(array)} [cb] - optional callback to perform on array
 * @returns {array} sorted array
 */
const sortByTimestamp = (array, cb) => {
  const newArray = cb ? cb(array) : [...array];
  return newArray.sort((a, b) => moment(b.timestamp).diff(a.timestamp));
};

const dateToISOString = (date) => new Date(parseInt(date, 10)).toISOString();

const getMonth = (date) => {
  try {
    return format(new Date(date), 'M');
  } catch {
    // For compatibility with previous moment behavior
    return 'Invalid date';
  }
};

export {
  getNoteDate,
  sortByDate,
  formatNoteDetailsDate,
  formatNoteDetailsDateTime,
  getPeriodByDays,
  getStartDate,
  checkDateValidity,
  formatDateMMDDYYYY,
  isValidTimeStamp,
  formatDateYYYYMMDD,
  formatDateMMMMD,
  timeStampToRelativeDate,
  sortByTimestamp,
  dateToISOString,
  getMonth,
  getUSFormatDate,
};
